import { AssignedToForSelect } from 'components/documentTemplates/GeneralSettings/types';
import { DocumentCategory } from 'gql-types';
import { formatFullName } from 'lib/helpers';
import {
    amountPayloadValueToLocalValue,
    amountValueToPayloadValue,
} from 'lib/numbers';
import {
    DocumentField,
    DocumentParameter,
} from 'services/documentFields/types';
import { activateDocumentTemplateAutomation } from 'services/documentTemplates';
import { getReviewProcess, getSignProcess } from 'services/documents/utils';
import { DocumentReviewProcess, DocumentSignProcess } from 'services/enums';
import { t } from 'ttag';
import { ITag } from 'ui/tags/tagsTypes';

import { Nullable } from '../../types/general';
import { IUser } from '../../types/user';
import { AutomationStatus } from './enums';
import {
    AssignedTo,
    Automation,
    Conditions,
    ConditionsKey,
    ConditionsValues,
    EditTemplateValues,
    FieldsSettings,
    Template,
    TemplateParticipant,
} from './types';

import { ConditionValueText } from './constants';

export type ParsedAmountConditionListValue = {
    ge: string;
    le: string;
};

const DOCUMENT_AMOUNT_CONDITION_STRING_SEPARATOR = '|';

export const parseAmountConditionListValue = (
    stringCondition: string,
): ParsedAmountConditionListValue => {
    const [ge = '', le = ''] = stringCondition.split(
        DOCUMENT_AMOUNT_CONDITION_STRING_SEPARATOR,
    );
    return { ge, le };
};

export const buildAmountConditionsFromListValue = (
    stringCondition: string,
): Conditions => {
    const { le, ge } = parseAmountConditionListValue(stringCondition);
    return {
        and: [
            le && {
                le: ['#document_amount', amountValueToPayloadValue(le)],
            },
            ge && {
                ge: ['#document_amount', amountValueToPayloadValue(ge)],
            },
        ].filter(Boolean),
    } as Conditions;
};

type ConditionBuilder = (values: string[]) => Conditions;

const conditionBuilderResolverMap: Partial<
    Record<ConditionsKey, ConditionBuilder>
> = {
    '#document_amount': (values) => {
        // allow only one amount condition per template
        const [stringCondition] = values;
        return buildAmountConditionsFromListValue(stringCondition);
    },
};

export const buildConditionsFromList = (
    conditions: ConditionsValues[],
): Conditions => {
    const map = new Map<ConditionsKey, string[]>();
    // group values over keys
    conditions.forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.map((item) =>
                map.set(key, (map.get(key) || []).concat([item])),
            );
            return;
        }
        map.set(key, (map.get(key) || []).concat([value as string]));
    });

    return {
        and: Array.from(map.entries()).map(([key, values]) => {
            const conditionBuilder = conditionBuilderResolverMap[key];

            if (conditionBuilder) {
                return conditionBuilder(values);
            }

            const uniqValues = [...new Set(values)];
            // combine value by `or` clause for the same condition key (#document_parameter)
            return {
                or: uniqValues.map((value) => {
                    return {
                        eq: [key, value] as ConditionsValues,
                    };
                }),
            };
        }),
    };
};

export const prepareConditionsForSubmit = (
    conditions: ConditionsValues[],
): Conditions | undefined => {
    if (!conditions) {
        return undefined;
    }
    const conditionsList = conditions.filter(([_, value]) =>
        Array.isArray(value) ? value.length : Boolean(value),
    );

    if (!conditionsList.length) {
        return undefined;
    }
    return buildConditionsFromList(conditionsList);
};

export const getConditionKeyText = (key: ConditionsKey): string => {
    switch (key) {
        case '#document_sign_process':
            return t`Процес підписання`;
        case '#document_side':
            return t`Документи`;
        case '#document_recipients.edrpou':
            return t`ЄДРПОУ/ІПН контрагента`;
        case '#document_category':
            return t`Тип документа`;
        case '#document_uploaded_by':
            return t`Завантажений`;
        case '#document_recipients.email':
            return t`Отриманий`;
        case '#document_amount':
            return t`Сума документа`;
        default:
            return '';
    }
};

export const composeAmountConditionListValue = ({
    le,
    ge,
}: ParsedAmountConditionListValue): string => {
    return [ge, le].join(DOCUMENT_AMOUNT_CONDITION_STRING_SEPARATOR);
};

export const documentAmountValueFormatter = (
    value: string | number,
): string => {
    return `${Number(value).toLocaleString()} ${t`грн`}`;
};

export const getConditionValueText = (
    key: ConditionsKey,
    value: string,
): string => {
    if (key === '#document_amount') {
        const { le, ge } = parseAmountConditionListValue(value);
        return [
            ge && t`Від: ` + documentAmountValueFormatter(ge),
            le && t`До: ` + documentAmountValueFormatter(le),
        ]
            .filter(Boolean)
            .join(' - ');
    }
    const block = ConditionValueText[key];
    if (!block) {
        return value;
    }
    return block[value] || value;
};

export const switchTemplateAutomationStatus = async (template: Template) => {
    const automation: Automation | undefined = template.automation[0];
    if (!automation) {
        return;
    }
    const isActive = automation.status === AutomationStatus.ENABLED;
    await activateDocumentTemplateAutomation(automation.id, !isActive);
};

export const fieldSettingToParameters = (
    fieldsSettings: Nullable<FieldsSettings>,
): DocumentParameter[] => {
    if (!fieldsSettings) {
        return [];
    }
    return fieldsSettings.fields.map((field) => ({
        fieldId: field.field_id,
        value: field.value,
        isRequired: field.is_required,
    }));
};

export const documentParametersToFieldsSetting = (
    parameters: DocumentParameter[],
): Nullable<FieldsSettings> => {
    if (parameters.length > 0) {
        return {
            fields: parameters.map((parameter) => ({
                field_id: parameter.fieldId,
                value: parameter.value === '' ? null : parameter.value,
                is_required: parameter.isRequired || false,
            })),
        };
    } else {
        return null;
    }
};

const isConditionsList = (
    operands: Conditions[] | ConditionsValues,
): operands is Conditions[] => {
    return !(
        operands.length === 2 &&
        (operands as ConditionsValues).every(
            (operand: any) => typeof operand === 'string',
        )
    );
};

/**
 * Find #document_amount condition and compose to local used (string) condition
 * @example: { and: [{ le: ['#document_amount', 1000] }, { ge: ['#document_amount', 199] }] } -> [['#document_amount', '1.99|10']]
 * @param conditions
 */
export const composeDocumentAmountConditionList = (
    conditions: Conditions[],
): ConditionsValues[] => {
    const {
        le: [leConditionKey, le],
    } = (conditions.find((item: any) => 'le' in item) || { le: [] }) as Record<
        'le',
        ConditionsValues
    >;
    const {
        ge: [geConditionKey, ge],
    } = (conditions.find((item: any) => 'ge' in item) || { ge: [] }) as Record<
        'ge',
        ConditionsValues
    >;

    if (
        leConditionKey === '#document_amount' ||
        geConditionKey === '#document_amount'
    ) {
        return [
            [
                '#document_amount',
                [
                    typeof ge === 'number'
                        ? amountPayloadValueToLocalValue(ge)
                        : '',
                    typeof le === 'number'
                        ? amountPayloadValueToLocalValue(le)
                        : '',
                ].join(DOCUMENT_AMOUNT_CONDITION_STRING_SEPARATOR),
            ],
        ];
    }
    return [];
};

export const buildConditionsList = (
    conditions?: Conditions,
): ConditionsValues[] => {
    // Extract list of condition values from condition expression ([#document_parameter, value])

    if (!conditions) {
        return [];
    }
    const operands = conditions.and || conditions.or || conditions.eq || [];

    if (isConditionsList(operands)) {
        const documentAmountConditionList = composeDocumentAmountConditionList(
            operands,
        );
        // catch #document_amount condition
        if (documentAmountConditionList.length) {
            return documentAmountConditionList;
        }

        // recursively get conditions values if every operands is condition
        return operands.reduce<ConditionsValues[]>(
            (acc, conditionsArg) =>
                acc.concat(buildConditionsList(conditionsArg)),
            [],
        );
    } else {
        return [operands];
    }
};

export const changeConditionsListDocCategoryIdToTitle = async (
    conditionsValue: ConditionsValues[],
    docCategoriesDetails?: DocumentCategory[],
) => {
    if (!docCategoriesDetails?.length) {
        return conditionsValue;
    }

    const changeConditionValueTitle = (value) => {
        if (value[0] !== '#document_category') {
            return value;
        }
        const categoryTitle =
            docCategoriesDetails.find(
                (docCategory) => String(docCategory.id) === String(value[1]),
            )?.title || value[1];

        return [value[0], categoryTitle];
    };

    return conditionsValue.map(changeConditionValueTitle);
};

export const buildITagList = (tags: string[]): ITag[] => {
    return tags.map((tag) => {
        return {
            id: '',
            name: tag,
        };
    });
};

// we need create array from same items
export const getArrayCondition = (
    conditions: ConditionsValues[],
): ConditionsValues[] => {
    const conditionUploadToArray = conditions
        .filter((item) => item[0] === '#document_uploaded_by')
        .map((item) => item[1]);
    const conditionRecipientsEmailToArray = conditions
        .filter((item) => item[0] === '#document_recipients.email')
        .map((item) => item[1]);

    const newConditions = conditions.filter(
        (item) =>
            item[0] !== '#document_uploaded_by' &&
            item[0] !== '#document_recipients.email',
    );

    if (conditionUploadToArray.length) {
        newConditions.push([
            '#document_uploaded_by',
            conditionUploadToArray as string[],
        ]);
    }
    if (conditionRecipientsEmailToArray.length) {
        newConditions.push([
            '#document_recipients.email',
            conditionRecipientsEmailToArray as string[],
        ]);
    }

    return newConditions;
};

const getSignersAndReviewers = (participants: TemplateParticipant[]) => {
    return participants.map((participant) => {
        if (participant.group?.id !== null) {
            return participant.group;
        } else return participant.role;
    });
};

export const getAssignedToForEditing = (
    assignedTo: Nullable<AssignedTo>,
    currentUser: IUser,
): AssignedToForSelect =>
    assignedTo
        ? {
              value: assignedTo.id,
              firstName: assignedTo.user.firstName,
              secondName: assignedTo.user.secondName,
              email: assignedTo.user.email,
          }
        : {
              value: currentUser.currentRole.id,
              firstName: currentUser.firstName,
              secondName: currentUser.secondName,
              email: currentUser.email,
          };

export const getEditTemplateValues = (
    template?: Template,
    currentUser: IUser,
): EditTemplateValues =>
    template
        ? {
              name: template.name,
              conditions:
                  template.automation.length > 0
                      ? getArrayCondition(
                            buildConditionsList(
                                template.automation[0].conditions,
                            ),
                        )
                      : [],
              isReviewRequired: template.reviewSettings
                  ? template.reviewSettings.is_required
                  : false,
              reviewProcess: getReviewProcess(
                  Boolean(
                      template.reviewSettings &&
                          template.reviewSettings.is_ordered,
                  ),
              ),
              reviewers: getSignersAndReviewers(template.reviewers),
              signProcess: getSignProcess(
                  Boolean(
                      template.signersSettings &&
                          template.signersSettings.is_ordered,
                  ),
              ),
              signers: getSignersAndReviewers(template.signers),
              viewers: template.viewerRoles,
              parameters: fieldSettingToParameters(template.fieldsSettings),
              tags: template.tagsSettings
                  ? buildITagList(template.tagsSettings.tags)
                  : [],
              assignedTo: getAssignedToForEditing(
                  template.assignedTo,
                  currentUser,
              ),
          }
        : {
              name: '',
              conditions: [],
              isReviewRequired: false,
              reviewProcess: DocumentReviewProcess.PARALLEL_PROCESS_TYPE,
              reviewers: [],
              signProcess: DocumentSignProcess.PARALLEL_PROCESS_TYPE,
              signers: [],
              viewers: [],
              parameters: [],
              tags: [],
              assignedTo: getAssignedToForEditing(null, currentUser),
          };

interface checkConditionsProps {
    currentUser: IUser;
    conditions: Conditions;
}

const findCondition = (
    obj: Record<string, unknown>,
    searchCondition: string,
    searchValue: string,
): boolean => {
    return Object.entries(obj).reduce((acc, [_, value]) => {
        if (acc) {
            return acc;
        }

        const isConditionWithCurrentValueExist =
            Array.isArray(value) &&
            typeof value[0] === 'string' &&
            value[0] === searchCondition &&
            value[1] === searchValue;

        if (isConditionWithCurrentValueExist) {
            return true;
        }

        return typeof value === 'object' && value
            ? findCondition(
                  value as Record<string, unknown>,
                  searchCondition,
                  searchValue,
              )
            : acc;
    }, false);
};

export const checkConditions = ({
    currentUser,
    conditions,
}: checkConditionsProps): Nullable<string> => {
    const currentCompanyEdrpou = currentUser.currentCompany.edrpou;
    const isInboxDocumentSide = findCondition(
        conditions,
        '#document_side',
        'inbox',
    );
    const isRecipientEdrpouCurrentCompany = findCondition(
        conditions,
        '#document_recipients.edrpou',
        currentCompanyEdrpou,
    );

    if (isInboxDocumentSide && isRecipientEdrpouCurrentCompany) {
        return t`Для вхідних документів можна вказувати лише ЄДРПОУ ваших контрагентів.`;
    }

    return null;
};

export const checkParameters = (
    parameters: DocumentParameter[],
    documentFields: DocumentField[],
) => {
    // check if selected params still exist in database
    const existingParameters = parameters.filter(({ fieldId }) =>
        documentFields.find(({ id }) => id === fieldId),
    );

    const isAllParametersExistInDatabase =
        existingParameters?.length === parameters.length;

    if (!isAllParametersExistInDatabase) {
        return t`Параметр документа не знайдено у базі даних`;
    }

    return null;
};

export const getCurrentDocumentTemplates = (
    pageNumber: number,
    templatesPerPage: number,
    documentTemplates: Template[],
): Template[] => {
    const pagesVisited = (pageNumber - 1) * templatesPerPage;
    const lastIndexDisplayedTemplates = pagesVisited + templatesPerPage;
    return documentTemplates.slice(pagesVisited, lastIndexDisplayedTemplates);
};

export const filterDocumentTemplateBySearch = (
    searchValue: string | null,
    template: Template,
): boolean => {
    // якщо значення пошуку порожнє, то показуємо всі сценарії
    if (!searchValue) {
        return true;
    }

    const matchString = searchValue.trim().toLowerCase();

    // пошук за назвою сценарію
    if (template.name.toLowerCase().includes(matchString)) {
        return true;
    }

    // пошук за співпадінням з повного ім'ям або email користувача
    const isUserMatchByEmailOrName = (user: Template['assignedTo']['user']) =>
        user.email?.toLowerCase().includes(matchString) ||
        formatFullName(user)?.toLowerCase().includes(matchString);

    return [
        // пошук за погоджувачами
        ...template.reviewers.map((item) => item.role.user),
        // пошук за підписантами
        ...template.signers.map((item) => item.role.user),
        // пошук за переглядачами
        ...template.viewerRoles.map((item) => item.user),
        // пошук за відповідальною особою
        template.assignedTo?.user,
    ]
        .filter(Boolean)
        .some(isUserMatchByEmailOrName);
};
